import { t } from 'ember-intl';
import { PhFilePdf } from 'ember-phosphor-icons';
import ReSelect from 'tio-ui/components/select';
import type { TOC } from '@ember/component/template-only';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';

/** One untagged upload plus whether its filing PATCH is currently in flight. */
export interface OtherDocumentRow {
  asset: TasAssetModel;
  isFiling: boolean;
}

interface OtherDocumentsListSignature {
  Args: {
    /** Live, correctly-classified uploads no categorized row can place (no
     * row key, no declared type — legacy-surface uploads). Empty → render
     * nothing. */
    rows: OtherDocumentRow[];
    /** The admin-configured document entries a document may be filed under. */
    entries: Array<{ label: string; type: string }>;
    /** Files `asset` under the entry with `label`: tags it like a categorized
     * upload so it moves into that row and counts toward the requirement. */
    onFile: (asset: TasAssetModel, label: string) => unknown;
    disabled?: boolean;
  };
  Element: HTMLDivElement;
}

const toSelectItems = (entries: Array<{ label: string; type: string }>) =>
  entries.map((entry) => ({ label: entry.label, key: entry.label }));

/**
 * "Other documents" tray below the document rows: the carryover population
 * the submit gate already counts (`untaggedLegacyUploads`) but the rows
 * cannot display — documents uploaded through legacy free-form surfaces
 * carry no row key or declared type, so without this tray they were
 * invisible here while still unblocking submission (#10226).
 *
 * Each document carries a "File under…" select of the configured categories:
 * the employee asserts what the document is — the applications flows'
 * use-anyways semantics — and the document moves into that row.
 */
const OtherDocumentsList: TOC<OtherDocumentsListSignature> = <template>
  {{#if @rows.length}}
    <div class="tio-col-extra-tight" data-test-other-documents ...attributes>
      <h4 class="tio-text-sm-semibold uppercase tracking-wider tio-fg-muted">
        {{t "tas.attachments.other_documents"}}
      </h4>
      <p class="text-xs tio-fg-muted">
        {{t "tas.attachments.other_documents_hint"}}
      </p>
      <div class="tio-col-extra-tight">
        {{#each @rows as |row|}}
          {{! Sized to match the document rows above — at text-xs with a 12px
            icon these read as an afterthought beside them, and the fixed
            max-width truncated filenames against empty space. Vertical padding
            stays modest here because the file-under select sets the row height. }}
          <div
            class="tio-row-between gap-3 rounded border tio-border-default tio-bg-surface px-3 py-2"
            data-test-other-document
          >
            <a
              href={{row.asset.signedUrl}}
              target="_blank"
              rel="noopener noreferrer"
              class="tio-inline-row gap-2 flex-1 min-w-0 tio-fg-secondary hover:tio-fg-info text-sm"
              title={{row.asset.filename}}
              data-test-other-document-link
            >
              <PhFilePdf @size="18" class="shrink-0" />
              <span class="truncate">{{row.asset.filename}}</span>
            </a>
            {{#if @entries.length}}
              <div class="w-48 shrink-0" data-test-other-document-file-under>
                {{! Disabled while this document's own PATCH is in flight. The
                  row deliberately stays here until the server accepts, so
                  without this the employee can pick a second category and fire
                  a competing write built from the same original tags. }}
                <ReSelect
                  @items={{toSelectItems @entries}}
                  @onChange={{fn @onFile row.asset}}
                  @clearable={{false}}
                  @disabled={{or @disabled row.isFiling}}
                  @placeholder={{t "tas.attachments.file_under"}}
                  {{! Every tray row renders one of these, so a bare "File
                    under…" leaves screen-reader users with N identically-named
                    controls and no way to tell which document each files. }}
                  @ariaLabel={{t "tas.attachments.file_under_document" filename=row.asset.filename}}
                />
              </div>
            {{/if}}
          </div>
        {{/each}}
      </div>
    </div>
  {{/if}}
</template>;

export default OtherDocumentsList;
